import { Configs } from '@/config';
import { sessionFromLoginResponse } from '@/auth/authSession';
import type { LoginRequestBody } from '@/auth/core/buildLoginBody';
import type { LoginError, LoginResult, LoginSuccessResponse } from '@/auth/types';

/**
 * Map HTTP status to a safe user-facing login error (no credential hints).
 * @param {number} status
 * @param {string | undefined} serverMessage
 * @returns {LoginError}
 */
export function mapLoginHttpError(status: number, serverMessage?: string): LoginError {
  if (status === 401) {
    return { code: 'invalid_credentials', message: 'Invalid email or password.' };
  }
  if (status === 429) {
    return {
      code: 'rate_limited',
      message: serverMessage?.trim() || 'Too many attempts. Please try again later.',
    };
  }
  if (status === 400 || status === 422) {
    return {
      code: 'validation',
      message: serverMessage?.trim() || 'Please check your email and password.',
    };
  }
  return { code: 'server', message: 'Something went wrong. Please try again.' };
}

/**
 * POST username/password login to coolture_auth.
 * @param {LoginRequestBody} body
 * @returns {Promise<LoginResult>}
 */
export async function postLogin(body: LoginRequestBody): Promise<LoginResult> {
  if (!Configs.loginApiUrl) {
    return {
      ok: false,
      error: {
        code: 'misconfigured',
        message: 'Auth service URL is not configured (VITE_AUTH_BASE_URL).',
      },
    };
  }

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Kincode-Client-Id': Configs.kincodeClientId,
  };

  if (Configs.unauthenticatedApiToken) {
    headers.Authorization = `Bearer ${Configs.unauthenticatedApiToken}`;
  }

  let response: Response;

  try {
    response = await fetch(Configs.loginApiUrl, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(body),
    });
  } catch {
    return {
      ok: false,
      error: { code: 'network', message: 'Unable to reach the auth service.' },
    };
  }

  if (!response.ok) {
    let message: string | undefined;
    try {
      const payload = (await response.json()) as { message?: string };
      message = payload?.message;
    } catch {
      message = undefined;
    }
    return { ok: false, error: mapLoginHttpError(response.status, message) };
  }

  let payload: LoginSuccessResponse;
  try {
    payload = (await response.json()) as LoginSuccessResponse;
  } catch {
    return {
      ok: false,
      error: { code: 'server', message: 'Unexpected response from auth service.' },
    };
  }

  if (!payload?.bearerToken || payload.companyID == null || payload.employeeID == null) {
    return {
      ok: false,
      error: { code: 'server', message: 'Incomplete login response from auth service.' },
    };
  }

  const mfaRequired = Boolean(payload.mfaRequired);

  return {
    ok: true,
    mfaRequired,
    session: sessionFromLoginResponse(payload),
  };
}
